import React from "react";
import {
  TextField,
  Chip,
  CircularProgress,
  Typography
} from "@material-ui/core";
import MuiAutocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import { FixedSizeList } from "react-window";

const LISTBOX_PADDING = 8; // px

function renderRow(props) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING
    }
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

const ListboxComponent = React.forwardRef((props, ref) => {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 36;

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemCount * itemSize;
  };

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <FixedSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={itemSize}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </FixedSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const useStyles = makeStyles(theme => ({
  root: {
    width: 210,
    marginRight: theme.spacing(2)
  },
  listbox: {
    boxSizing: "border-box",
    "& ul": {
      padding: 0,
      margin: 0
    }
  },
  paper: {
    borderRadius: 5
  }
}));

export default function Autocomplete({
  options = [],
  name,
  value,
  onChange,
  onInputChange,
  label,
  placeholder,
  size = "small",
  loading,
  clearable = true,
  enableInputValue,
  filterOptions,
  ...restProps
}) {
  const classes = useStyles();

  return (
    <MuiAutocomplete
      disableListWrap
      classes={classes}
      ListboxComponent={ListboxComponent}
      loading={loading}
      options={options}
      value={value}
      clearOnBlur={!enableInputValue}
      disableClearable={!clearable}
      filterOptions={filterOptions}
      inputValue={enableInputValue ? value?.name ?? "" : undefined}
      onChange={(event, value, reason, details) => {
        onChange?.({ target: { name, value: value || {} } }, reason, details);
      }}
      onInputChange={(event, value, reason) => {
        if (enableInputValue) {
          onChange?.(
            { target: { name, value: { id: value, name: value } } },
            reason
          );
        }
        onInputChange?.({ target: { name, value } }, reason);
      }}
      getOptionSelected={(option, value) =>
        option.id === value.id || option.id === value
      }
      getOptionLabel={option => option.name || ""}
      renderInput={params => (
        <TextField
          {...params}
          variant="outlined"
          size={size}
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={16} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
      renderOption={option => <Typography noWrap>{option.name}</Typography>}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip size={size} label={option.name} {...getTagProps({ index })} />
        ))
      }
      {...restProps}
    />
  );
}
